import { Injectable } from '@angular/core';
import {Observable, BehaviorSubject, map} from 'rxjs';
import { tap } from 'rxjs/operators';
import {ApiService, RemoteResponse} from './api.service';
import {User} from '../classes/user';
import {LocalStorageService} from './local-storage.service';
import {Router} from '@angular/router';
import {StudentsService} from './students.service';
import {Assignment} from '../classes/assignment';
import {LearningOutcome} from '../classes/learning-outcome';
import {LearningOutcomesService} from './learning-outcomes.service';
import {AssignmentsService} from './assignments.service';

/**
 * Service to handle user authentication and session management.
 * This service is responsible for login, logout, session retrieval, and user data management.
 * Provides an observable `currentUser` to monitor authentication state changes.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser: Observable<User | null>;

  constructor(
    private apiService: ApiService,
    private localStorage: LocalStorageService,
    private studentsService: StudentsService,
    private learningOutcomesService: LearningOutcomesService,
    private router: Router,
    private assignmentsService: AssignmentsService,
  ) {
    this.currentUserSubject = new BehaviorSubject<User | null>(this.localStorage.getUser());
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  login(credentials: { username: string; password: string }): Observable<User> {
    return this.apiService.post('login', credentials).pipe(
      //check for errors, if the response is null or not true, throw error
      tap(response => {
        if (response && response.success) {
          const user = this.getCurrentUser(response);
          this.localStorage.setUser(user);
          this.currentUserSubject.next(user);
          this.studentsService.getStudentsFromApi(response);
          this.learningOutcomesService.getLearningOutcomesFromApi(response);
          this.assignmentsService.getAssignmentsFromApi(response);
        }
      }),
      map(response => {
        //check for errors, if the response is not ok, throw error
        if (response && !response.success) {
          throw new Error(response.dataError[0]?.message || 'Login failed');
        }
        else if(!response){
          throw new Error('Service not available');
        }
        return this.getCurrentUser(response);
      }),
    );
  }

  getSession(): Observable<User> {
    return this.apiService.get('auth/session').pipe(
      tap(response => {
        const user: User = this.getCurrentUser(response);
        this.localStorage.setUser(user);
        this.currentUserSubject.next(user);
      }),
      map(response => {
        return this.getCurrentUser(response);
      })
    );
  }

  private getCurrentUser(response: RemoteResponse): User {
    const remoteData = response.data[0].dataSet;
    const user = new User();
    user.setUser({
      email: remoteData.email,
      firstName: remoteData.firstName,
      lastName: remoteData.lastName,
      requestKey: remoteData.requestKey,
      avatarUrl: remoteData.avatarUrl,
      learningOutcomes: remoteData.outcomes,
      assignments: remoteData.assignments.map((assignment: any): Assignment => {
        const assignmentObj = new Assignment();
        assignmentObj.setAssignment({
          requestKey: assignment.requestKey,
          name: assignment.name,
          learningOutcomes: assignment.outcomes.map((outcome: any): LearningOutcome => {
            const learningOutcome = new LearningOutcome();
            learningOutcome.setLearningOutcome({
              requestKey: outcome.requestKey,
              name: outcome.name,
            });
            return learningOutcome;
          }),
          dateAdded: assignment.dateAdded,
          createdBy: assignment.createdBy,
          students: [],
        });
        return assignmentObj;
      })
    });
    return user;
  }

  logout(): Observable<boolean> {
    return this.apiService.post('logout', {}).pipe(
      tap(() => {
        this.cleaSessionAndLogout();
      }),
      map(() => {
        return true;
      }),
    );
  }

  cleaSessionAndLogout(): void {
    this.localStorage.clearUser();
    this.currentUserSubject.next(null);
    this.router.navigate(['/login']);
  }

  refreshToken(): Observable<User> {
    return this.apiService.get('auth/refresh').pipe(
      tap(response => {
        //if the response is not empty, update the user
        if(response && response.success && response.data.length > 0 && response.data[0].dataSet && response.data[0].dataSet.requestKey){
          const user = this.getCurrentUser(response);
          this.localStorage.setUser(user);
          this.currentUserSubject.next(user);
        }
        else if(!response || !response.success){
          // if the response is empty or not successful, user is not authenticated
          this.localStorage.clearUser();
          this.currentUserSubject.next(null);
          this.router.navigate(['/login']);
        }
      }),
      map(response => {
        return this.getCurrentUser(response);
      })
    );
  }

  isAuthenticated(): boolean {
    return this.currentUserValue !== null && this.currentUserValue !== undefined;
  }
}

